"use client";

import React, {useMemo} from "react";
import {Context} from "@hotwired/stimulus";
import {useForm} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import * as v from "valibot";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController";
import {RhfInput} from "@root/server/client/esweb/components/ui/rhf/RhfInput";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission";
import {t} from "@root/server/client/esweb/src/lib/i18n";
import {TenantEditFormSchema} from "@root/server/client/esweb/src/pages/app/tenants/tenant_shemas";
import {ContinueSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";


export const name = "app--tenants--tenant_edit_form";

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <TenantEditFormStimulus {...props} />);
    }
}


type NewTenantFormValues = v.InferInput<typeof TenantEditFormSchema>;

interface NewTenantFormProps {
    submitUrl: string;
    initialValues?: Partial<NewTenantFormValues>;
}


function TenantEditFormStimulus({submitUrl, initialValues}: NewTenantFormProps) {
    const defaultValues = useMemo<NewTenantFormValues>(() => ({
        name: initialValues?.name ?? "",
        phone: initialValues?.phone ?? "",
        email: initialValues?.email ?? "",
        url: initialValues?.url ?? null,
        firmycz_url: initialValues?.firmycz_url ?? null,
    }), [initialValues]);

    const {control, handleSubmit, watch} = useForm<NewTenantFormValues>({
        resolver: valibotResolver(TenantEditFormSchema),
        defaultValues,
        mode: "onTouched",
        reValidateMode: "onChange",
    });

    const {execute, isSubmitting, serverErrors} = useFormSubmission<NewTenantFormValues>({submitUrl});

    return (
        <form className="space-y-6" onSubmit={handleSubmit(execute)}>
            <div className="space-y-4">
                <RhfInput
                    control={control}
                    name="name"
                    label={t({cs: "Název firmy", en: "Company name"})}
                    inputProps={{
                        type: "text",
                        placeholder: t({cs: "např. Stěhování Praha s.r.o.", en: "e.g. Moving Services Ltd"}),
                    }}
                />

                <RhfInput
                    control={control}
                    name="phone"
                    label={t({cs: "Telefon *", en: "Phone *"})}
                    inputProps={{
                        type: "tel",
                        placeholder: t({cs: "+420 123 456 789", en: "+420 123 456 789"}),
                        autoComplete: "tel",
                    }}
                />

                <RhfInput
                    control={control}
                    name="email"
                    label={t({cs: "E-mail *", en: "Email *"})}
                    inputProps={{
                        type: "email",
                        placeholder: t({cs: "kontakt@firma.cz", en: "contact@company.com"}),
                        autoComplete: "email",
                    }}
                />

                <RhfInput
                    control={control}
                    name="url"
                    label={t({cs: "Web", en: "Website"})}
                    nullable={true}
                    inputProps={{
                        type: "url",
                        placeholder: "https://",
                    }}
                />

                <RhfInput
                    control={control}
                    name="firmycz_url"
                    label={t({cs: "Firmy.cz profil", en: "Firmy.cz profile"})}
                    nullable={true}
                    inputProps={{
                        type: "url",
                        placeholder: "https://www.firmy.cz/detail/...",
                    }}
                />
            </div>

            <FormServerErrors errors={serverErrors} />

            <ContinueSubmitButton {...{isSubmitting}}/>
        </form>
    );
}
